<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center" @click="handleBackdropClick">
    <!-- Backdrop -->
    <div class="absolute inset-0" :class="backdropClass"></div>
    
    <!-- Dialog Content -->
    <div class="relative bg-surface-1 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col outline outline-1 outline-offset-[-1px] outline-border-default" @click.stop>
      <!-- Header -->
      <div :class="headerClasses">
        <slot name="header">
          <div class="flex justify-start items-center gap-2.5">
            <div class="justify-start text-base font-semibold leading-5" :class="variant === '2' ? 'modal-header-text' : 'text-text-default'">
              {{ title }}
            </div>
          </div>
        </slot>
        <div v-if="showCloseButton && !processing" class="absolute right-4 top-1/2 -translate-y-1/2 flex items-center">
          <i class="ri-close-line ri-xl cursor-pointer hover:opacity-70 transition-opacity" :class="variant === '2' ? 'modal-header-text opacity-80' : 'text-text-subtle'" @click="close()" />
        </div>
        <div v-else-if="showCloseButton && processing" class="absolute right-4 top-1/2 -translate-y-1/2 flex items-center">
          <i class="ri-close-line ri-xl opacity-30 cursor-not-allowed" :class="variant === '2' ? 'modal-header-text' : 'text-text-subtle'" />
        </div>
      </div>
      
      <!-- Content -->
      <div class="overflow-y-auto flex-1" :class="contentClasses">
        <slot></slot>
      </div>
      
      <!-- Footer -->
      <div v-if="showFooter || $slots.footer" :class="footerClasses">
        <slot name="footer">
          <template v-if="variant === '2'">
            <div class="w-full flex flex-wrap items-center justify-center gap-3">
              <!-- Unsaved indicator: fixed size, doesn't grow -->
              <div class="flex items-center gap-1.5 shrink-0 modal-header-text">
                <i class="ri-alert-line" />
                <span class="leading-4 tracking-tight whitespace-nowrap">{{ $t('common.modal.unsavedChanges') }}</span>
              </div>
              <div class="flex-1 flex items-center gap-2 min-w-[200px]">
                <div class="flex-1 h-10 px-4 py-2.5 rounded-md outline outline-1 outline-offset-[-1px] outline-border-default flex justify-center items-center transition-colors" :class="processing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-3'" @click="handleDiscard">
                  <span class="modal-header-text text-sm leading-4 tracking-tight">{{ $t('messaging.input.discard') }}</span>
                </div>
                <div class="flex-1 h-10 px-4 py-2.5 bg-btn-primary rounded-md flex justify-center items-center gap-2 transition-colors" :class="processing ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer hover:bg-btn-primary'" @click="handleSave">
                  <template v-if="processing">
                    <div class="fusion-loader loader-sm" />
                    <span class="text-white text-sm leading-4 tracking-tight">{{ $t('common.modal.saving') }}</span>
                  </template>
                  <template v-else>
                    <span class="text-white text-sm leading-4 tracking-tight">{{ $t('common.modal.save') }}</span>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </slot>
      </div>
    </div>
  </div>
</template>

<style>
  /* Modal header/footer — uses existing primitive tokens directly */
  .modal-header-bg {
    background-color: var(--background-modal-header);
  }
  .modal-header-text {
    color: var(--white);
  }
</style>

<script>
export default {
  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false
    },
    variant: {
      type: String,
      required: false,
      default: '1',
      validator(value) {
        return ['1', '2'].includes(value);
      }
    },
    showCloseButton: {
      type: Boolean,
      required: false,
      default: true
    },
    showFooter: {
      type: Boolean,
      required: false,
      default: false
    },
    processing: {
      type: Boolean,
      required: false,
      default: false
    },
    backdropType: {
      type: String,
      required: false,
      default: 'gray', // 'gray' or 'clear'
      validator(value) {
        return ['gray', 'clear'].includes(value);
      }
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    closeOnBackdropClick: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return {
      //TODO: REFACTOR THIS
      overlay_key: 'dialog_modal_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    }
  },
  mounted() {
    window.event_bus.$on('CLOSE-OVERLAYS', this.handleCloseOverlays);
  },
  beforeUnmount() {
    window.event_bus.$off('CLOSE-OVERLAYS', this.handleCloseOverlays);
  },
  computed: {
    backdropClass() {
      return this.backdropType === 'gray' ? 'bg-black/50' : 'bg-transparent';
    },
    headerClasses() {
      if (this.variant === '2')
        return 'modal-header-bg px-4 py-3 relative border-b border-border-default';
      
      return 'p-6 pb-2 relative';
    },
    contentClasses() {
      if (this.variant === '2')
        return 'p-4';
      
      return 'p-6 pt-2';
    },
    footerClasses() {
      if (this.variant === '2')
        return 'self-stretch px-6 py-3 modal-header-bg border-t border-border-separator inline-flex justify-start items-center gap-4';
      
      return 'p-6 pt-4 border-t border-border-separator';
    }
  },
  methods: {
    handleCloseOverlays(payload) {
      // Check if this overlay should be excluded
      const excludeKey = payload?.excludeKey;
      if (excludeKey && excludeKey === this.overlay_key)
        return;
      
      // Only close if the modal is actually open
      if (this.isOpen && !this.processing)
        this.$emit('close');
    },
    close() {
      if (this.processing) return;
      this.$emit('close');
    },
    handleBackdropClick() {
      if (this.processing) return;
      if (this.closeOnBackdropClick) {
        this.close();
      }
    },
    handleDiscard() {
      if (this.processing) return;
      this.close();
    },
    handleSave() {
      if (this.processing) return;
      this.$emit('save');
    }
  }
}
</script>
